import { useCallback, useEffect, useRef, useState } from "react";
import { useDirectUpload } from "@/hooks/use-direct-upload";

export interface UseImageUploadFieldOptions {
  /** Server-provided image shown before any upload (never revoked; also the failed-upload fallback). */
  initialPreviewUrl?: string | null;
  /** Receives the signed blob id once a selected file finishes uploading — write it into the form. */
  onUploaded: (signedId: string) => void;
  /** Fires after a failed upload, once the preview has been reset to the initial image. */
  onUploadError?: () => void;
  /** Fires after `onRemove` clears the preview — clear the form's signed id (and set any remove flag). */
  onRemoved?: () => void;
}

export interface UseImageUploadFieldResult {
  /** Current preview: a local blob URL for a pending upload, or a server/external URL. */
  previewUrl: string | null;
  isUploading: boolean;
  /** Upload progress 0–100 for the in-flight file. */
  progress: number;
  /** Preview the file immediately (object URL) and direct-upload it, reporting the signed id via `onUploaded`. */
  onFileSelect: (file: File) => void;
  /** Clear the preview (revoking any blob URL) and fire `onRemoved`. */
  onRemove: () => void;
  /** Replace the preview with an external URL (or null), revoking any pending blob URL. No callbacks fire. */
  setPreview: (url: string | null) => void;
}

/**
 * Single-image picker state machine shared by every "pick a photo → preview it → direct-upload →
 * stash the signed id in the form" page. Owns the object-URL lifecycle: at most one blob URL is
 * alive, replaced URLs are revoked eagerly, and the survivor is revoked on unmount. Callers only
 * decide where the signed id goes (form field names and remove flags differ per page).
 */
export function useImageUploadField(options: UseImageUploadFieldOptions): UseImageUploadFieldResult {
  const { upload, isUploading, progress } = useDirectUpload();
  const [previewUrl, setPreviewUrl] = useState<string | null>(options.initialPreviewUrl ?? null);
  const blobUrlRef = useRef<string | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(
    () => () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    },
    [],
  );

  const setPreview = useCallback((url: string | null) => {
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    blobUrlRef.current = null;
    setPreviewUrl(url);
  }, []);

  const onFileSelect = useCallback(
    (file: File) => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      const blobUrl = URL.createObjectURL(file);
      blobUrlRef.current = blobUrl;
      setPreviewUrl(blobUrl);

      upload(file).then(
        (signedId) => {
          // The preview moved on (removed, replaced, or a different tile was
          // picked) while this upload was in flight — its signed id is stale
          // and must not reach the form.
          if (blobUrlRef.current !== blobUrl) return;
          optionsRef.current.onUploaded(signedId);
        },
        () => {
          // A newer selection owns the preview now; leave it alone.
          if (blobUrlRef.current !== blobUrl) return;
          setPreview(optionsRef.current.initialPreviewUrl ?? null);
          optionsRef.current.onUploadError?.();
        },
      );
    },
    [upload, setPreview],
  );

  const onRemove = useCallback(() => {
    setPreview(null);
    optionsRef.current.onRemoved?.();
  }, [setPreview]);

  return { previewUrl, isUploading, progress, onFileSelect, onRemove, setPreview };
}
